// CatalogStreamsCard — UniversityBrandAdminPage (Courses tab) settings card.
// Toggles which course streams this org's CreateCourseModal pickers may pull
// from: standard / elite / university / community / church(faith). Persisted
// per-org at facilities.branding.catalog_streams via a read-modify-write
// merge (never clobbers sibling branding keys). Absent config = ALL streams
// on, so existing orgs keep today's behaviour until an admin saves toggles.
// The collapsible catalog preview lists what is currently visible and reuses
// the shipped CoursePreviewModal — the same outline preview the pickers use.
import { useState, useEffect } from 'react'
import { Loader2, ChevronDown, Eye, Save } from 'lucide-react'
import { supabase } from '@/integrations/supabase/client'
import CoursePreviewModal from './CoursePreviewModal'
import { ALL_STREAMS, DEFAULT_STREAMS, classifyStream, classVisibleInStreams, normalizeCatalogStreams, saveOrgCatalogStreams, UNIVERSITY_FACILITY_ID } from '@/utils/catalogStreams'
import type { CatalogStream } from '@/utils/catalogStreams'
import { useFacilityBranding } from '@/contexts/SubdomainBrandingContext'
import { renderFormattedText, shouldShowAge } from '@/utils/textClean'

const CARD = '#0d0d15'
const BR = 'rgba(255,255,255,0.10)'
const TX = '#ffffff'
const MU = 'rgba(255,255,255,0.55)'
const MU2 = 'rgba(255,255,255,0.35)'
const GR = '#4ade80'
const G = 'var(--org-accent, #3b82f6)'
const M = '#06060c'

const STREAM_COLORS: Record<string, string> = {
  standard: '#38bdf8', elite: '#f59e0b', university: '#a78bfa',
  community: '#4a7c5e', church: '#d97706',
}

type Row = {
  id: string; name: string; source: 'master' | 'community'
  subject_group?: string | null; curriculum_track?: string | null
  target_age?: number | null; author_name?: string | null
}

export default function CatalogStreamsCard({ facilityId }: { facilityId: string | null }) {
  const [saved, setSaved] = useState<CatalogStream[]>(DEFAULT_STREAMS)
  const [draft, setDraft] = useState<CatalogStream[]>(DEFAULT_STREAMS)
  const [rows, setRows] = useState<Row[]>([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null)
  const [expanded, setExpanded] = useState(false)
  const [preview, setPreview] = useState<Row | null>(null)

  const branding = useFacilityBranding()
  const showAge = shouldShowAge(branding?.org_type)

  useEffect(() => {
    if (!facilityId) return
    let alive = true
    ;(async () => {
      const [facRes, clsRes, uniRes, libRes] = await Promise.all([
        supabase.from('facilities').select('branding').eq('id', facilityId).maybeSingle(),
        // Master K-12 catalog — facility 0001
        supabase.from('classes')
          .select('id, name, subject_group, curriculum_track, target_age, in_church_catalog, facility_id')
          .eq('facility_id', '00000000-0000-0000-0000-000000000001')
          .eq('is_active', true)
          .order('name'),
        // University catalog — facility 0002
        supabase.from('classes')
          .select('id, name, subject_group, curriculum_track, target_age, in_church_catalog, facility_id')
          .eq('facility_id', UNIVERSITY_FACILITY_ID)
          .eq('is_active', true)
          .order('name'),
        // Community library
        supabase.from('community_course_library' as any)
          .select('id, name, subject_group, target_age, author_name')
          .order('name', { ascending: true })
          .limit(60),
      ])
      if (!alive) return
      const streams = normalizeCatalogStreams((facRes.data as any)?.branding?.catalog_streams)
      setSaved(streams); setDraft(streams)
      const master = ((clsRes.data ?? []) as any[]).map(c => ({ ...c, source: 'master' as const }))
      const uni    = ((uniRes.data  ?? []) as any[]).map(c => ({ ...c, source: 'master' as const }))
      const community = ((libRes.data ?? []) as any[]).map(c => ({ ...c, source: 'community' as const }))
      setRows([...master, ...uni, ...community])
      setLoading(false)
    })()
    return () => { alive = false }
  }, [facilityId])

  const toggle = (k: CatalogStream) =>
    setDraft(d => (d.includes(k) ? d.filter(x => x !== k) : [...d, k]))

  const dirty = JSON.stringify([...draft].sort()) !== JSON.stringify([...saved].sort())

  const onSave = async () => {
    if (!facilityId || !draft.length) return
    setSaving(true); setMsg(null)
    try {
      await saveOrgCatalogStreams(facilityId, draft)
      setSaved(draft)
      setMsg({ ok: true, text: `Saved, ${draft.length} stream${draft.length === 1 ? '' : 's'} enabled. Pickers update the next time they open.` })
    } catch (e: any) {
      setMsg({ ok: false, text: `Save failed: ${e?.message || 'unknown error'}` })
    } finally {
      setSaving(false)
    }
  }

  const visible = rows.filter(c => classVisibleInStreams(c, draft))
  const counts = ALL_STREAMS.map(s => ({ ...s, n: rows.filter(c => classifyStream(c) === s.key).length }))

  if (!facilityId) return null

  return (
    <div style={{
      background: CARD, border: `1px solid ${BR}`, ['--org-accent' as any]: ((branding as any)?.primary_color || (branding as any)?.secondary_color || (branding as any)?.accent_color || '#3b82f6'), borderRadius: 20,
      padding: 'clamp(16px, 3vw, 24px) clamp(16px, 3.5vw, 28px)',
      marginBottom: 20,
    }}>
      <h2 style={{ fontSize: 16, fontWeight: 800, color: TX, margin: '0 0 4px' }}>Catalog Streams</h2>
      <p style={{ fontSize: 12.5, color: MU, margin: '0 0 18px', lineHeight: 1.6 }}>
        Choose which course streams your organisation can pull from when adding courses (the Master Catalog and
        Community Library pickers). Admin-side curation only, it never changes what enrolled learners already see.
      </p>

      {loading ? (
        <div style={{ padding: 18, textAlign: 'center', color: MU2, fontSize: 13 }}>
          <Loader2 size={18} className="animate-spin" style={{ margin: '0 auto 8px', display: 'block' }} /> Loading catalog settings...
        </div>
      ) : (
        <>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 16 }}>
            {counts.map(s => {
              const on = draft.includes(s.key)
              const col = STREAM_COLORS[s.key] || GR
              return (
                <button key={s.key} type="button" onClick={() => toggle(s.key)} title={s.hint}
                  style={{
                    padding: '8px 14px', borderRadius: 999, cursor: 'pointer',
                    border: `2px solid ${on ? col : BR}`,
                    background: on ? `${col}18` : 'transparent',
                    color: on ? col : MU, fontWeight: 700, fontSize: 12.5,
                    display: 'inline-flex', alignItems: 'center', gap: 7,
                  }}>
                  <span style={{ width: 7, height: 7, borderRadius: '50%', background: on ? col : MU2 }} />
                  {s.label}{s.n > 0 && <span style={{ fontWeight: 600, opacity: 0.7 }}>{s.n}</span>}
                </button>
              )
            })}
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
            <button onClick={onSave} disabled={!dirty || saving || !draft.length}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 8, padding: '10px 22px', borderRadius: 12,
                border: 'none', cursor: dirty && !saving && !!draft.length ? 'pointer' : 'default',
                background: dirty && !saving && !!draft.length ? G : 'rgba(255,255,255,0.06)',
                color: dirty && !saving && !!draft.length ? M : MU2, fontWeight: 800, fontSize: 13,
              }}>
              {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
              {saving ? 'Saving...' : 'Save streams'}
            </button>
            {msg && <span style={{ fontSize: 12.5, fontWeight: 700, color: msg.ok ? GR : '#f87171' }}>{msg.text}</span>}
            {!msg && !dirty && <span style={{ fontSize: 12.5, color: MU2 }}>All streams on, nothing hidden.</span>}
            {draft.length === 0 && <span style={{ fontSize: 12.5, color: '#f87171', fontWeight: 700 }}>Enable at least one stream to save.</span>}
          </div>

          {/* Collapsible catalog preview — what the toggles actually gate */}
          <div style={{ marginTop: 16, borderTop: '1px solid rgba(255,255,255,0.06)', paddingTop: 14 }}>
            <button type="button" onClick={() => setExpanded(x => !x)}
              style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, background: 'transparent', border: 'none', color: MU, fontSize: 12.5, fontWeight: 700, cursor: 'pointer', padding: 0 }}>
              <span style={{ textAlign: 'left', lineHeight: 1.4 }}>
                {visible.length} of {rows.length} catalog {rows.length === 1 ? 'course' : 'courses'} visible to this org, {expanded ? 'hide' : 'browse & preview'}
              </span>
              <ChevronDown size={15} style={{ transform: expanded ? 'rotate(180deg)' : 'none', flexShrink: 0 }} />
            </button>
            {expanded && (
              <div style={{ marginTop: 10, maxHeight: 300, overflowY: 'auto', border: `1px solid ${BR}`, borderRadius: 12, padding: 4 }}>
                {visible.length === 0 ? (
                  <div style={{ padding: '22px 16px', textAlign: 'center', color: MU2, fontSize: 12.5 }}>
                    Nothing visible, enable a stream above.
                  </div>
                ) : visible.map(c => {
                  const st = classifyStream(c)
                  const col = STREAM_COLORS[st] || GR
                  const meta = ALL_STREAMS.find(s => s.key === st)
                  return (
                    <div key={`${c.source}-${c.id}`} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, padding: '9px 12px', borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                          <span style={{ fontSize: 9, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.06em', padding: '2px 7px', borderRadius: 20, background: `${col}18`, color: col, flexShrink: 0 }}>
                            {meta?.label ?? st}
                          </span>
                          <span style={{ fontSize: 13, fontWeight: 600, color: TX, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {renderFormattedText(c.name)}
                          </span>
                        </div>
                        <div style={{ fontSize: 10.5, color: MU2, marginTop: 2 }}>
                          {c.source === 'community'
                            ? `Community library${c.author_name ? ` · by ${c.author_name}` : ''}`
                            : 'Master catalog'}
                          {showAge && c.target_age ? ` · Age ${c.target_age}+` : ''}
                        </div>
                      </div>
                      <button type="button" onClick={() => setPreview(c)}
                        style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '7px 13px', borderRadius: 10, border: '1px solid rgba(74,222,128,0.4)', background: 'rgba(74,222,128,0.07)', color: GR, fontWeight: 700, fontSize: 11.5, cursor: 'pointer', flexShrink: 0 }}>
                        <Eye size={13} /> Preview
                      </button>
                    </div>
                  )
                })}
              </div>
            )}
          </div>
        </>
      )}

      {preview && (
        <CoursePreviewModal
          course={{
            id: preview.id, name: preview.name,
            source: preview.source,
            subject_group: preview.subject_group ?? null,
            curriculum_track: preview.curriculum_track ?? null,
            target_age: preview.target_age ?? null,
          }}
          onClose={() => setPreview(null)}
        />
      )}
    </div>
  )
}